import { useAppDispatch, useAppSelector } from '@hooks/app/redux';
import useMyWorkspaces from '@hooks/query/provider/useMyWorkspaces';
import { memberApiV2 } from '@lib/api';
import { actions } from '@store/ducks/e-visits';
import { Select } from '@ui';
import React from 'react';
import { useRouteMatch } from 'react-router-dom';
import { OptionTypeBase } from 'react-select';
import { TUserMedicalInstitution } from 'types/users';
import { IMyWorkspaceItem } from 'types/workspace.types';

interface IRouteParams {
  workspaceId?: string;
}
interface EVisitsWorkspaceSelectorProps {
  disabled?: boolean;
  onChange?: (w?: TUserMedicalInstitution | null) => void;
  hasEVisits?: boolean | null;
}

const EVisitsWorkspaceSelector: React.FunctionComponent<EVisitsWorkspaceSelectorProps> = ({
  disabled,
  onChange,
  hasEVisits = null,
}) => {
  const dispatch = useAppDispatch();
  const { params } = useRouteMatch<IRouteParams>();
  const mounted = React.useRef(false);

  const { data: myWorkspaces, isFetching } = useMyWorkspaces({ hasEVisits });

  const providerId = useAppSelector((state) => state.users.currentProvider?.id)!;
  const selectedInstitution = useAppSelector((state) => state.eVisits.selectedInstitution);

  const workspacesOption = React.useMemo(
    () =>
      (myWorkspaces ?? []).map((w) => ({
        ...w,
        label: `${w.name} (${w.expertise?.name ?? '?'})`,
        value: w.id,
      })),
    [myWorkspaces],
  );

  const currentOption = React.useMemo(
    () => workspacesOption.find((o) => o.value === selectedInstitution?.id),
    [workspacesOption, selectedInstitution?.id],
  );

  const setSelectedInstitution = React.useCallback(
    async (value?: (OptionTypeBase & IMyWorkspaceItem) | null) => {
      if (value) {
        const response = await memberApiV2.getWorkspace(providerId, value.value);
        const next = { ...response.data.data, hasEvisit: value.hasEvisit };

        dispatch(actions.setSelectedInstitution(next));
        onChange?.(next);
      } else {
        dispatch(actions.setSelectedInstitution(undefined));
        onChange?.(undefined);
      }
    },
    [dispatch, onChange],
  );

  //   // If no selected, select first or from ID from params
  React.useEffect(() => {
    if (!currentOption && myWorkspaces && myWorkspaces.length > 0) {
      if (params.workspaceId) {
        const selected = myWorkspaces.find((w) => w.id === params.workspaceId);
        setSelectedInstitution(selected ? ({ value: selected.id, ...selected } as any) : undefined);
      } else {
        setSelectedInstitution({ value: myWorkspaces[0].id, ...myWorkspaces[0] } as any);
      }
    }
  }, [currentOption, myWorkspaces, params.workspaceId]);

  React.useEffect(() => {
    if (providerId && mounted.current) {
      setSelectedInstitution(undefined);
    }
  }, [providerId]);

  React.useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  return (
    <Select<OptionTypeBase & IMyWorkspaceItem>
      options={workspacesOption}
      value={currentOption}
      disabled={disabled}
      isLoading={isFetching}
      onChange={setSelectedInstitution}
      className="e-visits-workspace-selector"
    />
  );
};

export default EVisitsWorkspaceSelector;
